import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { useTheme, styled } from '@mui/material/styles';
import { Box, Drawer, Grid, Stack, Typography, Tab, Tabs, Chip, List as MuiList, ListItem, Button } from '@mui/material';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import {
  Add,
  ArrowDown2,
  ArrowUp2,
  NotificationStatus,
  Trash
  //  Trash
} from 'iconsax-react';
import { ThemeMode } from 'types/config';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import StockSelector from 'pages/watchlist/StockSelector';
import { IStock, PriceList, WatchListStock } from 'types/custom/stock';
import { useDataContext } from 'contexts/DataContext';
import serverCall from 'api/serverCall';
import api from 'api/api';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import { TransactionType } from 'enum/transaction-type';
import PlaceOrderModal from 'pages/orders/components/PlaceOrderModal';
import UserNotActiveAlert from 'components/alert/UsersAreNotActive';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useStatusContext } from 'contexts/StatusContext';
import { LiveTicks } from 'types/custom/websocket';

interface TabPanelProps {
  children?: ReactNode;
  index: string | number;
  value: string | number;
  watchLists: WatchListStock[];
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, watchLists, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

const StyledGridOverlay = styled(Stack)(({ theme }) => ({
  height: '400px',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[200] : theme.palette.secondary[400]
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.secondary.light
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary[200]
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.dark : theme.palette.common.white
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === ThemeMode.DARK ? '0.09' : '0.95',
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.darker : theme.palette.secondary.light
  }
}));

const ListItemContent = ({
  stock,
  index,
  handleDelete,
  handlePlaceOrder,
  ticks
}: {
  stock: PriceList;
  index: number;
  handleDelete: (stockId: string, type: 'add' | 'delete') => void;
  handlePlaceOrder: (stockId: string, transactionType: TransactionType) => void;
  ticks: React.MutableRefObject<LiveTicks>;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleMouseEnter = () => {
    setOpen(true);
  };

  const handleMouseLeave = () => {
    setOpen(false);
  };

  const gainAndLossPrice = ((ticks.current[stock.stockId]?.price as any) ?? stock?.price) - stock.closingPrice;
  const gainAndLossPercentage = (gainAndLossPrice / stock.closingPrice) * 100;

  return (
    <Draggable draggableId={index.toString()} index={index} key={index}>
      {(provided: any, snapshot: any) => (
        <ListItem
          style={{ ...provided.draggableProps.style, cursor: snapshot.isDragging ? 'all-scroll' : 'auto' }}
          divider
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ cursor: 'all-scroll', p: 0, m: 0 }}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Grid item container sx={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
            <Grid md={6} item>
              <Typography variant="subtitle1" component="div">
                <Typography
                  variant="subtitle1"
                  component="span"
                  color={gainAndLossPrice === 0 ? '' : gainAndLossPrice > 0 ? '#4caf50' : '#C50D0D'}
                >
                  {stock.stockId}
                </Typography>
                <Chip
                  size="small"
                  label={
                    <Typography color={'#808080'} variant="body2" sx={{ fontSize: '0.5rem', fontWeight: 'bold' }}>
                      {stock.exchange}
                    </Typography>
                  }
                  sx={{ borderRadius: 1, p: 0, backgroundColor: 'transparent', mt: 0.5 }}
                />
              </Typography>
            </Grid>
            <Grid item md={6} container alignItems={'center'} justifyContent={'end'}>
              <Typography sx={{ width: '2.3rem' }} variant="body2" component="div" color={'#A6A6A6'}>
                {gainAndLossPrice === null || isNaN(gainAndLossPrice) || gainAndLossPrice === undefined ? '_' : gainAndLossPrice.toFixed(2)}
              </Typography>
              <Typography sx={{ ml: 1, width: '2.5rem' }} variant="body2" component="div" color={'#9B9B9B'}>
                {gainAndLossPercentage === null || isNaN(gainAndLossPercentage) || gainAndLossPercentage === undefined
                  ? '_'
                  : gainAndLossPercentage.toFixed(2) + '%'}
              </Typography>
              <Typography color={gainAndLossPrice === 0 ? '#9B9B9B' : ''} sx={{ ml: 1, width: '2rem' }}>
                {gainAndLossPrice !== 0 ? (
                  <> {gainAndLossPrice > 0 ? <ArrowUp2 size="18" color="#4caf50" /> : <ArrowDown2 size="18" color="#C50D0D" />}</>
                ) : (
                  '_'
                )}
              </Typography>
              <Typography
                sx={{ width: '2rem' }}
                variant="body2"
                component="div"
                color={gainAndLossPrice === 0 ? '#9B9B9B' : gainAndLossPrice > 0 ? '#4caf50' : '#C50D0D'}
              >
                {(ticks.current[stock.stockId]?.price as any) ?? stock?.price}
              </Typography>
            </Grid>

            {open && (
              <Box sx={{ position: 'absolute', bottom: 10, right: 2 }}>
                {/* <IconButton
                  onClick={() => {
                    handlePlaceOrder(stock.stockId, TransactionType.BUY);
                  }}
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  B
                </IconButton>

                <IconButton
                  onClick={() => {
                    handlePlaceOrder(stock.stockId, TransactionType.SELL);
                  }}
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{ mr: 1 }}
                >
                  S
                </IconButton> */}

                <IconButton
                  onClick={() => {
                    handleDelete(stock.stockId, 'delete');
                  }}
                  variant="light"
                  color="error"
                  size="small"
                >
                  <Trash />
                </IconButton>
              </Box>
            )}
          </Grid>
        </ListItem>
      )}
    </Draggable>
  );
};

const renderEmptyState = (focusSelectStockInput: () => void) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      height: '100%'
    }}
  >
    <Typography sx={{ mt: 5 }} variant="subtitle1" color="textSecondary">
      Nothing here
    </Typography>
    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
      Use the search bar to add stocks.
    </Typography>
    <Button sx={{ mt: 2 }} onClick={focusSelectStockInput} variant="contained" startIcon={<Add />} size="medium">
      Add stock
    </Button>
  </Box>
);

const WatchListDrawer = () => {
  const theme = useTheme();
  const [value, setValue] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<IStock | null>(null);
  const [watchLists, setWatchLists] = useState<WatchListStock[]>([]);
  const [currentWatchList, setCurrentWatchList] = useState<WatchListStock | null>(null);
  const [stockSelectOpen, setStockSelectOpen] = useState<boolean>(false);
  const [isPlaceOrder, setIsPlaceOrder] = useState<boolean>(false);
  const [numberOfStocks, setNumberOfStocks] = useState<number | string>('');
  const [noUsersActive, setNoUsersActive] = useState<boolean>(false);
  const [, forceRerender] = useState(0);
  const { stocks, selectedUsers } = useDataContext();
  const { kiteStatus } = useStatusContext();
  const { liveTicks } = useWebsocketContext();

  const handleToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    const id = setInterval(() => {
      forceRerender((x) => x + 1);
    }, 250); // 4 FPS
    return () => clearInterval(id);
  }, []);

  const fetchAllWatchLists = (flag: string) => {
    serverCall.get(api.getAllWatchLists(), (response: WatchListStock[], error) => {
      if (error) {
        console.error(error);
      } else {
        setWatchLists(response);
        if (response.length > 0 && flag === 'fetch') {
          setCurrentWatchList(response[0]!);
        } else {
          setCurrentWatchList(response[value]!);
        }
      }
    });
  };

  useEffect(() => {
    fetchAllWatchLists('fetch');
  }, [kiteStatus]);

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setCurrentWatchList(watchLists[newValue]!);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  const handlePlaceOrder = (stockId: string, transactionType: TransactionType) => {
    if (selectedUsers.length === 0) {
      dispatch(
        openSnackbar({
          open: true,
          close: true,
          message: `At least select one user to execute an order`,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        })
      );
      return;
    }
    const selectedStock = stocks.filter((item) => item.stockId === stockId);
    setSelectedStock(selectedStock[0] as IStock);
    setOpen(false);
    setIsPlaceOrder(true);
  };

  const handleSaveWatchList = (stockId: string | null, type: 'add' | 'delete' | 'save', stockIds?: string[]) => {
    let currentStockIds: string[] | null = null;

    if (type === 'add') {
      currentStockIds = [...(currentWatchList?.stockIds || []), stockId!];
    } else if (type === 'delete') {
      currentStockIds = (currentWatchList?.stockIds || []).filter((id) => id !== stockId);
    } else if (type === 'save' && stockIds) {
      currentStockIds = stockIds;
    }

    const requestBody = currentStockIds
      ? {
          watchListId: currentWatchList?.watchListId,
          watchListName: currentWatchList?.watchListName,
          stockIds: currentStockIds
        }
      : null;

    serverCall.post(api.saveWatchList(), requestBody, (response, error) => {
      if (error) {
        console.error(error);
      } else {
        if (response) {
          setSelectedStock(null);
          fetchAllWatchLists('re-fetch');
        }
      }
    });
  };

  const focusSelectStockInput = () => {
    setStockSelectOpen(true);
  };

  const handleCloseNoUserActive = () => {
    setNoUsersActive(false);
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;

    const updatedStockIds = Array.from(currentWatchList?.stockIds ?? []);
    const updatedPriceList = Array.from(currentWatchList?.priceList ?? []);

    const [movedStockId] = updatedStockIds.splice(source.index, 1);
    const [movedPrice] = updatedPriceList.splice(source.index, 1);

    updatedStockIds.splice(destination.index, 0, movedStockId);
    updatedPriceList.splice(destination.index, 0, movedPrice);

    const updatedCurrentWatchList = {
      ...currentWatchList,
      stockIds: updatedStockIds,
      priceList: updatedPriceList
    } as WatchListStock;

    setCurrentWatchList(updatedCurrentWatchList);

    setWatchLists((prevWatchLists) =>
      prevWatchLists.map((watchList) => (watchList.watchListId === currentWatchList?.watchListId ? updatedCurrentWatchList : watchList))
    );

    handleSaveWatchList(null, 'save', updatedStockIds);
  };

  return (
    <>
      {isPlaceOrder && (
        <PlaceOrderModal
          open={isPlaceOrder}
          handleClose={setIsPlaceOrder}
          selectedStock={selectedStock}
          setSelectedStock={setSelectedStock}
          numberOfStocks={numberOfStocks}
          setNumberOfStocks={setNumberOfStocks}
          selectedUsers={selectedUsers}
          setNoUsersActive={setNoUsersActive}
        />
      )}
      {noUsersActive && <UserNotActiveAlert handleClose={handleCloseNoUserActive} open={noUsersActive} />}
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          color="secondary"
          variant="light"
          onClick={handleToggle}
          aria-label="settings toggler"
          size="large"
          sx={{ color: 'secondary.main', bgcolor: open ? iconBackColorOpen : iconBackColor, p: 1 }}
        >
          <NotificationStatus variant="Bulk" />
        </IconButton>
      </Box>
      <Drawer sx={{ zIndex: 2001 }} anchor="right" onClose={handleToggle} open={open} PaperProps={{ sx: { width: { xs: 350, sm: 474 } } }}>
        {open && (
          <MainCard content={false} sx={{ border: 'none', borderRadius: 0, height: '100vh' }}>
            <SimpleBar
              sx={{
                '& .simplebar-content': {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h5">Watch List</Typography>
                  <IconButton color="secondary" sx={{ p: 0 }} onClick={handleToggle}>
                    <Add size={28} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
                {watchLists && watchLists.length !== 0 ? (
                  <Grid container spacing={1.5} sx={{ mt: 2 }}>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                          value={value}
                          onChange={handleChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          aria-label="basic tabs example"
                        >
                          {watchLists.map((item, i) => (
                            <Tab
                              key={i}
                              label={item.watchListName}
                              icon={<Chip label={item.stockIds.length || 0} variant="filled" size="small" />}
                              iconPosition="end"
                              {...a11yProps(i)}
                            />
                          ))}
                        </Tabs>
                      </Box>
                      <Grid sx={{ px: 2.5, py: 1.5 }}>
                        <StockSelector
                          handleAddToWatchList={handleSaveWatchList}
                          selectedStock={selectedStock!}
                          setSelectedStock={setSelectedStock}
                          stocks={stocks}
                          stockSelectOpen={stockSelectOpen}
                          setStockSelectOpen={setStockSelectOpen}
                        />
                      </Grid>
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="droppable">
                          {(provided: any, snapshot: any) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className={snapshot.isDraggingOver ? 'dragover' : ''}>
                              {watchLists.map((item, i) => {
                                return (
                                  <TabPanel key={i} value={value} index={i} watchLists={watchLists}>
                                    {item?.stockIds?.length === 0 ? (
                                      <Box sx={{ width: '100%', height: '80vh' }}>{renderEmptyState(focusSelectStockInput)}</Box>
                                    ) : (
                                      <Box sx={{ width: '100%', height: '80vh' }}>
                                        <MuiList disablePadding sx={{ '& .MuiListItem-root': { px: 3, py: 1.5 } }}>
                                          {item.priceList.map((priceListItem, j) => (
                                            <ListItemContent
                                              stock={priceListItem}
                                              key={j}
                                              index={j}
                                              ticks={liveTicks}
                                              handleDelete={handleSaveWatchList}
                                              handlePlaceOrder={handlePlaceOrder}
                                            />
                                          ))}
                                        </MuiList>
                                      </Box>
                                    )}
                                  </TabPanel>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Box>
                  </Grid>
                ) : (
                  <Grid>
                    <StyledGridOverlay alignItems="center" justifyContent="center" spacing={1}>
                      <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
                        <g fill="none" fillRule="evenodd">
                          <g transform="translate(24 31.67)">
                            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
                            <path
                              className="ant-empty-img-1"
                              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                            />
                            <path
                              className="ant-empty-img-2"
                              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                            />
                            <path
                              className="ant-empty-img-3"
                              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                            />
                          </g>
                          <path
                            className="ant-empty-img-3"
                            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                          />
                          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                          </g>
                        </g>
                      </svg>
                      <Typography align="center">Oops! Something went wrong.</Typography>
                    </StyledGridOverlay>
                  </Grid>
                )}
              </Box>
            </SimpleBar>
          </MainCard>
        )}
      </Drawer>
    </>
  );
};

export default WatchListDrawer;
